import { z } from "zod";

import { TrackerEntityKind } from "@/enum/tracker.enum";

/**
 * List-detail cron/tracker schemas, extracted from `list.ts` so the schema file stays under
 * the size ratchet; re-exported from there for backward-compatible imports. (Distinct from
 * the leaner `cron-job.ts` CronJobSchema used by other surfaces.)
 */
export const CronJobSchema = z.object({
	id: z.number(),
	listId: z.number(),
	trackerId: z.number().nullable(),
	columnConfigId: z.number().nullable(),
	jobId: z.string(),
	status: z.string(),
	lastActivity: z.string().nullable(),
	error: z.string().nullable(),
	name: z.string(),
	createdById: z.number(),
	deletedById: z.number().nullable(),
	createdAt: z.string(),
	updatedAt: z.string(),
	deletedAt: z.string().nullable(),
});

export const TrackerFileInfoSchema = z.object({
	id: z.number(),
	name: z.string(),
	trackerId: z.number(),
	isImported: z.boolean(),
	amount: z.number(),
	createdById: z.number(),
	content: z.string().optional(),
	configuration: z
		.array(
			z.object({
				foundField: z.string(),
				assignedField: z.string(),
			})
		)
		.optional(),
	// PREC-3417 per-source accounting. Optional so a payload from before the change parses.
	entityKind: z.nativeEnum(TrackerEntityKind).optional(),
	// SQL counts arrive as strings, so coerce rather than trusting the declared type
	// (apps/web/CLAUDE.md §6).
	rowCount: z.coerce.number().optional(),
	readyCount: z.coerce.number().optional(),
	skippedCount: z.coerce.number().optional(),
	isActive: z.boolean().optional(),
});

export type TrackerFileInfo = z.infer<typeof TrackerFileInfoSchema>;

export const TrackerInfoSchema = z.object({
	id: z.number(),
	name: z.string(),
	updatedAt: z.string().transform((arg) => new Date(arg)),
	organizationCount: z.number().optional(),
	lastSignal: z.string().nullable().optional(),
	people: z.number().optional(),
	trackerFiles: z.array(TrackerFileInfoSchema),
});
